import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export function Field({ label, children, hint, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#101820] font-medium">{label}{required && <span className="text-[#C76A32]"> *</span>}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function TextField({ label, value, onChange, type = "text", testid, required, placeholder }) {
  return (
    <Field label={label} required={required}>
      <Input data-testid={testid} type={type} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="bg-white" />
    </Field>
  );
}

export function AreaField({ label, value, onChange, max, testid, required }) {
  return (
    <Field label={label} required={required} hint={max ? `${(value || "").length}/${max} characters` : undefined}>
      <Textarea data-testid={testid} value={value || ""} maxLength={max} rows={5}
        onChange={(e) => onChange(e.target.value)} className="bg-white resize-none" />
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, testid, required, placeholder = "Select…" }) {
  return (
    <Field label={label} required={required}>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function YesNo({ label, value, onChange, testid, required }) {
  return (
    <Field label={label} required={required}>
      <div className="flex gap-3">
        {["yes", "no"].map((v) => (
          <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
            className={`px-6 py-2 rounded-full border text-sm font-medium capitalize transition-all ${
              value === v ? "bg-[#101820] text-[#FAF6EE] border-[#101820]" : "bg-white text-[#101820] border-[#C79A3B]/40 hover:border-[#C79A3B]"}`}>
            {v}
          </button>
        ))}
      </div>
    </Field>
  );
}

export function CheckGroup({ label, options, values = [], onChange, testid }) {
  const toggle = (o) => onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);
  return (
    <Field label={label}>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
        {options.map((o) => (
          <label key={o} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer transition-all ${
              values.includes(o) ? "bg-[#C79A3B]/15 border-[#C79A3B]" : "bg-white border-[#C79A3B]/25 hover:border-[#C79A3B]/60"}`}>
            <Checkbox checked={values.includes(o)} onCheckedChange={() => toggle(o)} /> {o}
          </label>
        ))}
      </div>
    </Field>
  );
}

export function RatingRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-[#C79A3B]/15 last:border-0">
      <span className="text-sm text-[#101820]">{label}</span>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
            className={`w-8 h-8 rounded-md text-sm font-semibold transition-all ${
              (value || 0) >= n ? "bg-[#C79A3B] text-[#101820]" : "bg-white text-muted-foreground border border-[#C79A3B]/25 hover:border-[#C79A3B]"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
